import _ from 'lodash';
import React, { useState, useEffect, useMemo } from 'react';
import { useObserver } from 'mobx-react-lite';

import { Box, Flex, Text } from 'rebass/styled-components';
import styled from 'styled-components';
import { width } from 'styled-system';
import { mediaQueries } from 'shared/styles';
import { observer } from 'mobx-react';

import { useIncentives } from 'src/payments/hooks/use-incentives/use-incentives';

import { PBBIncentiveBannerWithCTA } from 'src/payments/components/pbb-incentive-banner/pbb-incentive-banner';

import MobileOnly from 'shared/components/mobile-only';
import { SkeletonLoader } from 'shared/components';
import { useCheckoutLineItems } from 'checkout/hooks/use-checkout-line-items';
import useViewport from 'hooks/use-viewport';
import useStores from 'shared/hooks/use-stores';
import PhoneNumberConfirmationModal from 'shared/modals/phone-number-confirmation';
import { ModalWrapper } from 'shared/modals/v2-wrapper';
import useMediaQuery from 'shared/hooks/use-media-query';
import RestrictedDiscountApplied from 'src/components/cart/restricted-discount-applied';

import { useAllotment } from 'checkout/hooks/allotment-context';
import TaxModal from './components/tax-modal';
import ConvenienceFeeModal from './components/convenience-fee-modal';
import CostTable from './components/cost-table';
import DispensaryInfo from './components/dispensary-info';
import PromoCodeField from './components/promo-code';
import Submit from './submit';
import CaliforniaWarning from './components/california-warning';
import TipField from './components/tip-field';

import { getCustomCheckoutMessage } from './helpers';
import { PaymentSelection } from '../sections/payment/selection';
import MMURAlertBanner from './mmur-alert-banner';

function Confirmation() {
  const { Cart, UI } = useStores();
  const viewport = useViewport();
  const { order, isDelivery, orderType } = Cart;
  const isCalculating = useObserver(() => Cart.isCalculating);
  const { dispensary } = order;
  const [taxModalOpen, setTaxModalOpen] = useState(false);
  const [convenienceFeeModalOpen, setConvenienceFeeModalOpen] = useState(false);
  // For Colorado region’s mandated warning ENG-63305
  const [isColoradoDispensary, setIsColoradoDispensary] = useState(false);
  // For Florida region’s explicit consent compliance requirements for guest checkout ENG-39985
  const [isFloridaDispensary, setIsFloridaDispensary] = useState(false);
  const lineItemData = useCheckoutLineItems();
  const isLargePhoneOrSmaller = useMediaQuery(mediaQueries.largePhone);

  const containerProps = {
    isIframe: UI.isIframe,
    iframeOffset: viewport.offset,
    parentOffset: viewport.parentOffset,
    viewportHeight: viewport.height,
    viewportWidth: viewport.width,
    iframeHeight: UI.iframeHeight,
  };

  const salesTaxExists = _.find(lineItemData, ['type', 'salesTax'])?.value > 0;
  const cannabisTaxExists = _.find(lineItemData, ['type', 'cannabisTax'])?.value > 0;
  const bottleDepositTaxExists = _.find(lineItemData, ['type', 'bottleDepositTaxCents'])?.value > 0;

  const showDeliveryDriverTip =
    isDelivery && dispensary.acceptsTips && !['cash', 'check'].includes(Cart.order.paymentMethod);
  const showDutchiePayTip = dispensary.acceptsDutchiePayTips && Cart.order.paymentMethod === 'dutchiePay';
  const { hideRoundingLineItem } = dispensary.storeSettings;

  const showTipField = showDeliveryDriverTip || showDutchiePayTip;

  const isCaliforniaDispensary = _.includes(['ca', 'california'], _.toLower(dispensary?.location?.state));

  const customCheckoutMessage = getCustomCheckoutMessage(dispensary, orderType);

  const { showCTABanner, incentiveValue, shouldShowCashBack } = useIncentives();
  const { allotmentInfo, isAllotmentExceeded } = useAllotment();

  useEffect(() => {
    const {
      dispensary: { location },
    } = Cart;
    if (location?.state === 'CO') {
      setIsColoradoDispensary(true);
    }
    if (location?.state === 'FL') {
      setIsFloridaDispensary(true);
    }
  }, [Cart]);

  const orderTotal = _.find(lineItemData, ['type', 'total'])?.value;
  const displayedOrderTotal = useMemo(() => `$${(orderTotal || 0).toFixed(2)}`, [orderTotal]);

  const submitButtonContents = useMemo(
    () =>
      isLargePhoneOrSmaller ? (
        <ExperimentButtonContents>
          <ExperimentButtonText>Place order</ExperimentButtonText>
          <ExperimentButtonTotal>{displayedOrderTotal}</ExperimentButtonTotal>
        </ExperimentButtonContents>
      ) : undefined,
    [isLargePhoneOrSmaller, displayedOrderTotal]
  );

  return (
    <ConfirmationWrap isEmbedded={UI.isVariant(`embedded`)} data-testid='checkout-confirmation-section'>
      <ConfirmationBox>
        <TaxModal
          salesTaxExists={salesTaxExists}
          cannabisTaxExists={cannabisTaxExists}
          bottleDepositTaxExists={bottleDepositTaxExists}
          receiptLineItems={lineItemData}
          onClose={() => setTaxModalOpen(false)}
          open={taxModalOpen}
        />
        <ConvenienceFeeModal onClose={() => setConvenienceFeeModalOpen(false)} open={convenienceFeeModalOpen} />
        <ModalWrapper open={UI.phoneNumberConfirmationObj.open}>
          <PhoneNumberConfirmationModal containerProps={containerProps} />
        </ModalWrapper>
        <DispensaryInfo dispensary={dispensary} />
        <RestrictedDiscountApplied />
        {customCheckoutMessage && (
          <CheckoutMessage>
            <CheckoutMessageScroll
              dangerouslySetInnerHTML={{
                __html: customCheckoutMessage.replace(/\n/g, '<br />'),
              }}
            />
          </CheckoutMessage>
        )}
        <TableContainer pt='20px'>
          <CostTable
            salesTaxExists={salesTaxExists}
            cannabisTaxExists={cannabisTaxExists}
            bottleDepositTaxExists={bottleDepositTaxExists}
            receiptLineItems={lineItemData}
            openTaxModal={() => setTaxModalOpen(true)}
            hideRoundingLineItem={hideRoundingLineItem}
          />
          <PromoCodeField />
        </TableContainer>
        {showTipField && <TipField />}
        {showCTABanner && (
          <MobileOnly>
            <PBBIncentiveBannerWithCTA />
          </MobileOnly>
        )}
        <MMURAlertBanner allotmentInfo={allotmentInfo} />
        <StyledFlex
          pt='20px'
          isFloridaDispensary={isFloridaDispensary}
          isAnonymous={Cart.saveGuestInfo || Cart.guestCustomer}
          includesPaymentSelection={isLargePhoneOrSmaller}
        >
          <TotalText width={[1 / 2]}>Order total:</TotalText>
          <TotalText data-testid='order-total' width={[1 / 2]} textAlign='right'>
            {isCalculating ? <SkeletonLoader height='28' /> : displayedOrderTotal}
          </TotalText>
        </StyledFlex>

        {shouldShowCashBack && (
          <CashBackContainer pt='16px'>
            <CashBackText width={[2 / 3]}>Pay by Bank cash-back:</CashBackText>
            <CashBackAmount width={[1 / 3]} textAlign='right'>
              {isCalculating ? (
                <SkeletonLoader height='28' data-testid='skeleton-loader' />
              ) : (
                `$${(incentiveValue || 0).toFixed(2)}`
              )}
            </CashBackAmount>
          </CashBackContainer>
        )}

        {isLargePhoneOrSmaller && (
          <StyledPaymentSelectionContainer>
            <PaymentSelection displayedInConfirmation />
          </StyledPaymentSelectionContainer>
        )}
        <Submit
          isColoradoDispensary={isColoradoDispensary}
          isFloridaDispensary={isFloridaDispensary}
          isCaliforniaDispensary={isCaliforniaDispensary}
          isAllotmentExceeded={isAllotmentExceeded}
          submitButtonContents={submitButtonContents}
        />
      </ConfirmationBox>
      {isCaliforniaDispensary && (
        <FooterContainer>
          <CaliforniaWarning />
        </FooterContainer>
      )}
    </ConfirmationWrap>
  );
}

export default observer(Confirmation);

const ConfirmationWrap = styled(Box)`
  font-family: ${({ theme }) => theme.customized.fonts.secondary};
  padding-bottom: 0;
  position: ${(props) => (props.isEmbedded ? 'relative' : 'sticky')};
  top: 53px;

  @media ${mediaQueries.desktop} {
    padding-bottom: 50px;
    top: 0;
  }

  @media ${mediaQueries.tablet} {
    padding-bottom: 34px;
  }

  @media ${mediaQueries.largePhone} {
    background-color: #f0f3f6;
    width: 100%;
    padding-bottom: ${(props) => (props.isEmbedded ? '125px' : '34px')};
  }
`;

const ConfirmationBox = styled(Box)`
  background-color: #f0f3f6;
  border-radius: ${({ theme }) => theme.customized.radius.md};
  max-width: 307px;
  padding: 29px 25px 34px 25px;
  position: relative;

  @media ${mediaQueries.desktop} {
    margin: 0 auto;
    max-width: 390px;
  }

  @media ${mediaQueries.largePhone} {
    max-width: 450px;
    padding: 34px 30px 0 30px;
    top: 0;
  }

  margin-bottom: 20px !important;
`;

const CheckoutMessage = styled.div`
  border-top: 1px solid #d3dadf;
  color: #646d72;
  font-size: 13px;
  line-height: ${18 / 13};
  padding: 20px 0;
`;

const CheckoutMessageScroll = styled.div`
  max-height: 248px;
  overflow-y: auto;
`;

const TableContainer = styled(Box)`
  border-top: 1px solid #d3dadf;
  border-bottom: 1px solid #d3dadf;
  color: #606369;
`;

const TotalText = styled(Text)`
  ${width}
  text-transform: uppercase;
  color: #606369;
  font-size: 16px;
  font-weight: bold;
`;

const FooterContainer = styled.div`
  max-width: 390px;
  margin: 0 auto;

  @media ${mediaQueries.largePhone} {
    padding: 0 30px;
  }
`;

const StyledFlex = styled(Flex)`
  border-bottom: ${(props) =>
    (props.isFloridaDispensary && props.isAnonymous) || props.includesPaymentSelection ? '1px solid #d3dadf' : null};
  padding-bottom: ${(props) =>
    (props.isFloridaDispensary && props.isAnonymous) || props.includesPaymentSelection ? '23px' : '0'};
`;

const StyledPaymentSelectionContainer = styled(Flex)`
  padding-top: 23px;
`;

const ExperimentButtonContents = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;

const ExperimentButtonText = styled.div`
  font-size: 16px;
  text-transform: none !important;
`;

const ExperimentButtonTotal = styled.div`
  font-size: 16px;
  font-weight: 400;
  line-height: normal;
`;

const CashBackContainer = styled(Flex)`
  /* Match the green styling from PBB incentive banner */
`;

const CashBackText = styled(Text)`
  text-transform: none;
  color: ${({ theme }) => theme.colors.dutchiePayGreen || '#00A47C'};
  font-size: 13px;
  font-weight: normal;
  ${width}
`;

const CashBackAmount = styled(Text)`
  ${width}
  color: ${({ theme }) => theme.colors.dutchiePayGreen};
  font-size: 13px;
  font-weight: bold;
`;
